import { useCallback, useEffect, useRef } from 'react';
import {
  trackCalculatorCompleted,
  trackCalculatorOpened,
  trackCalculatorResultViewed,
  trackCalculatorStarted,
} from '../lib/analytics';

export default function useCalculatorAnalytics({
  calculatorId,
  calculatorName,
}) {
  const startedRef = useRef(false);
  const completedRef = useRef(false);
  const viewedResultRef = useRef(false);
  const interactedFieldsRef = useRef(new Set());

  const baseParams = { calculatorId, calculatorName };

  useEffect(() => {
    trackCalculatorOpened(baseParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatorId, calculatorName]);

  const trackFieldInteraction = useCallback(
    (fieldName) => {
      if (!startedRef.current) {
        startedRef.current = true;
        trackCalculatorStarted({ ...baseParams, calculator_step: 'inputs' });
      }

      if (interactedFieldsRef.current.has(fieldName)) return;
      interactedFieldsRef.current.add(fieldName);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calculatorId, calculatorName]
  );

  const trackResultView = useCallback(
    (params = {}) => {
      if (viewedResultRef.current) return;
      viewedResultRef.current = true;
      trackCalculatorResultViewed({ ...baseParams, calculator_step: 'results', ...params });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calculatorId, calculatorName]
  );

  const trackComplete = useCallback(
    (params = {}) => {
      if (completedRef.current) return;

      if (!startedRef.current) return;
      completedRef.current = true;
      trackCalculatorCompleted({ ...baseParams, calculator_step: 'results', ...params });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calculatorId, calculatorName]
  );

  const trackReset = useCallback(() => {
    startedRef.current = false;
    completedRef.current = false;
    viewedResultRef.current = false;
    interactedFieldsRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatorId, calculatorName]);

  return {
    trackComplete,
    trackFieldInteraction,
    trackReset,
    trackResultView,
  };
}
